import * as React from "react";
import { CartesianGrid, ComposedChart, ResponsiveContainer, XAxis } from "recharts";
import { styled } from "styled-components";
import { useMediaQuery, useTheme } from "@wayflyer/flyui";
import { ChartFilterSwitches, ChartLegendContainer, getChartConfig } from "@wayflyer/flyui-dataviz";

import { Spacer } from "../../../../components/shared";
import { Spinner } from "../../../../components/styled";

export interface ConsumersSummaryGraphProps {
  data;
  legend;
  chartFilters;
  lines;
  bars;
  getTooltip;
  loading?: boolean;
  height?: number;
  //used for testing, to ensure responsiveContainer has a forced width and can then render the chart for snapshots
  width?: number | null | string;
}

const ChartTopRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 16px;
`;

const FlexContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

export const ConsumersSummaryGraph = ({
  data,
  legend,
  chartFilters,
  lines,
  bars,
  getTooltip,
  width = null, // null leaves height determine width, >0 is just used for snapshots to ensure chart is rendered
  height,
  loading = false,
}: ConsumersSummaryGraphProps): UIElement => {
  const query = useMediaQuery();
  const mediumWidth = query("sm").windowGt;
  const chartConfig = getChartConfig(mediumWidth);
  const theme = useTheme();

  if (loading) {
    return <Spinner />;
  }

  const xAxisProps = {
    tick: { fontSize: 12 },
    dataKey: "day",
  };

  return (
    <ChartLegendContainer>
      <ChartTopRow>
        <FlexContainer>
          <Spacer />
          {legend}
          {chartFilters ? <ChartFilterSwitches {...chartFilters} /> : null}
        </FlexContainer>
      </ChartTopRow>
      <ResponsiveContainer height={height ?? chartConfig.height} width={(width ?? "99%") as number}>
        <ComposedChart
          data={data}
          margin={{
            top: 24,
            right: 30,
            left: 30,
            bottom: 10,
          }}
        >
          {mediumWidth ? (
            <CartesianGrid
              strokeWidth={0.5}
              stroke={theme.palette.bg.neutral.strong}
              {...({ yAxisId: "left" } as any)}
            />
          ) : null}
          {mediumWidth ? <XAxis {...xAxisProps} /> : null}
          {mediumWidth ? getTooltip : null}
          {bars}
          {lines}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartLegendContainer>
  );
};
